import { useState } from 'react'
import { api } from '../api/client'

/**
 * "Glemt adgangskode?" på login-siden (beslutning 078).
 *
 * To trin: e-mailen, og så koden fra mailen sammen med den nye adgangskode. Svaret
 * på første trin er det samme, uanset om kontoen findes - ellers kunne siden bruges
 * til at finde ud af, hvem der har en konto. Derfor står der "hvis", ikke "vi har".
 *
 * En kode frem for et link: på en iPhone åbner et link fra mailen i Safari og ikke i
 * appen på hjemmeskærmen.
 */
export function PasswordResetForm({
  initialEmail,
  onDone,
  onCancel
}: {
  initialEmail: string
  onDone: (message: string) => void
  onCancel: () => void
}) {
  const [stage, setStage] = useState<'email' | 'code'>('email')
  const [email, setEmail] = useState(initialEmail)
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [repeat, setRepeat] = useState('')
  const [message, setMessage] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function requestCode(event?: React.FormEvent) {
    event?.preventDefault()
    setMessage(null)
    setBusy(true)
    try {
      await api.post<void>('/api/auth/password-reset', { email: email.trim() })
      setStage('code')
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Koden kunne ikke sendes.')
    } finally {
      setBusy(false)
    }
  }

  async function confirm(event: React.FormEvent) {
    event.preventDefault()
    setMessage(null)

    if (password !== repeat) {
      setMessage('De to adgangskoder er ikke ens.')
      return
    }

    setBusy(true)
    try {
      await api.post<void>('/api/auth/password-reset/confirm', {
        email: email.trim(),
        code: code.trim(),
        newPassword: password
      })
      onDone('Adgangskoden er skiftet. Log ind med den nye.')
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Adgangskoden kunne ikke skiftes.')
    } finally {
      setBusy(false)
    }
  }

  if (stage === 'email') {
    return (
      <form onSubmit={(e) => void requestCode(e)} style={{ textAlign: 'left', marginTop: 18 }}>
        <p className="muted small" style={{ marginBottom: 14 }}>
          Skriv din e-mail, så sender vi en kode, du kan sætte en ny adgangskode med.
        </p>

        <label className="field">
          <span className="field__label">E-mail</span>
          <input
            type="email"
            autoComplete="username"
            autoFocus
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>

        {message && (
          <div className="error-box" style={{ marginBottom: 12 }}>
            {message}
          </div>
        )}

        <button className="btn btn--primary" style={{ width: '100%' }} type="submit" disabled={busy}>
          {busy ? 'Sender ...' : 'Send kode'}
        </button>

        <p className="tiny muted" style={{ marginTop: 12 }}>
          Logger du ind med Microsoft, skiftes adgangskoden hos Microsoft - ikke her.
        </p>

        <button type="button" className="btn btn--link tiny" onClick={onCancel}>
          Tilbage til login
        </button>
      </form>
    )
  }

  return (
    <form onSubmit={(e) => void confirm(e)} style={{ textAlign: 'left', marginTop: 18 }}>
      <p className="muted small" style={{ marginBottom: 14 }}>
        Har <strong>{email.trim()}</strong> en konto med adgangskode, er der sendt en kode dertil. Den virker
        i 30 minutter. Kommer den ikke, så kig i uønsket post, eller bed om en ny om et minut.
      </p>

      <label className="field">
        <span className="field__label">Kode fra mailen</span>
        <input
          autoComplete="one-time-code"
          autoCapitalize="characters"
          autoFocus
          required
          placeholder="K7MQ-4XPA"
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
      </label>

      <label className="field">
        <span className="field__label">Ny adgangskode</span>
        <input
          type="password"
          autoComplete="new-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <span className="tiny muted">Mindst 8 tegn med både bogstaver og tal.</span>
      </label>

      <label className="field">
        <span className="field__label">Gentag ny adgangskode</span>
        <input
          type="password"
          autoComplete="new-password"
          required
          value={repeat}
          onChange={(e) => setRepeat(e.target.value)}
        />
      </label>

      {message && (
        <div className="error-box" style={{ marginBottom: 12 }}>
          {message}
        </div>
      )}

      <button className="btn btn--primary" style={{ width: '100%' }} type="submit" disabled={busy}>
        {busy ? 'Gemmer ...' : 'Sæt ny adgangskode'}
      </button>

      <div className="row" style={{ justifyContent: 'space-between', marginTop: 12 }}>
        <button type="button" className="btn btn--link tiny" disabled={busy} onClick={() => void requestCode()}>
          Send en ny kode
        </button>
        <button type="button" className="btn btn--link tiny" onClick={onCancel}>
          Tilbage til login
        </button>
      </div>
    </form>
  )
}
